import React, {
    useEffect,
    useState,
    useCallback,
    useRef
} from 'react';

import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import SSHTerminal from '../../components/SSHTerminal/SSHTerminal';
import { closeTunnel } from '../../services/DeviceDetailsAPI';
import { TunnelState } from '../../utils/tunnelState';
import './SSHTerminalPage.css';

const SSHTerminalPage = () => {
    const location = useLocation();

    const [tunnelData, setTunnelData] = useState(null);
    const [isDuplicateTab, setIsDuplicateTab] = useState(false);

    // Track ownership independently of React state.
    const ownsLockRef = useRef(false);
    const isDuplicateTabRef = useRef(false);
    const exitHandledRef = useRef(false);
    const tabIdRef = useRef(null);
    const lockKeyRef = useRef(null);

    // Notify DeviceDetails only when the original terminal exits.
    const notifyTerminalClosed = useCallback(() => {
        try {
            if (typeof BroadcastChannel !== 'undefined') {
                const channel = new BroadcastChannel(
                    'ssh-terminal-channel'
                );

                channel.postMessage({
                    type: 'SSH_TERMINAL_CLOSED'
                });

                channel.close();
            }

            localStorage.setItem(
                'ssh-terminal-closed',
                Date.now().toString()
            );
        } catch (error) {
            console.error(
                'Failed to notify DeviceDetails:',
                error
            );
        }
    }, []);

    // Release only the lock owned by this tab.
    const releaseOwnLock = useCallback(() => {
        if (!ownsLockRef.current) {
            return;
        }

        const lockKey = lockKeyRef.current;
        const tabId = tabIdRef.current;

        try {
            if (
                lockKey &&
                tabId &&
                localStorage.getItem(lockKey) === tabId
            ) {
                localStorage.removeItem(lockKey);
            }
        } catch (error) {
            console.error('Failed to release SSH lock:', error);
        }

        ownsLockRef.current = false;
    }, []);

    // Claim the lock or reject this tab as a duplicate.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const activeTunnel = TunnelState.getActiveTunnel();

        const tunnelId =
            params.get('tunnelId') ||
            activeTunnel?.tunnelId;

        if (!tunnelId) {
            return;
        }

        // Keep this key identical for every tab using the same tunnel.
        const lockKey = `ssh-terminal-lock-${tunnelId}`;
        const tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

        lockKeyRef.current = lockKey;
        tabIdRef.current = tabId;

        let existingTabId;

        try {
            existingTabId = localStorage.getItem(lockKey);
        } catch (error) {
            console.error('Unable to check SSH terminal lock:', error);
            toast.error('Unable to verify SSH terminal session.');
            setIsDuplicateTab(true);
            isDuplicateTabRef.current = true;
            return;
        }

        if (existingTabId) {
            // Critical: this tab does NOT own the lock.
            isDuplicateTabRef.current = true;
            ownsLockRef.current = false;
            setIsDuplicateTab(true);

            toast.error(
                'SSH terminal is already open in another tab.'
            );

            // Do not clear TunnelState, close the tunnel,
            // release the lock, or notify DeviceDetails.
            return;
        }

        try {
            localStorage.setItem(lockKey, tabId);

            // Verify ownership before allowing this terminal to operate.
            if (localStorage.getItem(lockKey) !== tabId) {
                isDuplicateTabRef.current = true;
                setIsDuplicateTab(true);
                return;
            }

            ownsLockRef.current = true;
        } catch (error) {
            console.error('Unable to claim SSH terminal lock:', error);
            setIsDuplicateTab(true);
            isDuplicateTabRef.current = true;
            return;
        }

        return () => {
            // Cleanup cannot remove another tab's lock.
            releaseOwnLock();
        };
    }, [location.search, releaseOwnLock]);

    // Load the terminal's tunnel details, including the relay session ID.
    useEffect(() => {
        const params = new URLSearchParams(location.search);

        const thingName = params.get('thingName');
        const tunnelId = params.get('tunnelId');
        const sourceToken = params.get('sourceToken');
        const region = params.get('region');
        const querySessionId = params.get('sessionId');

        if (thingName && tunnelId && sourceToken && region) {
            setTunnelData({
                thingName,
                tunnelId,
                sourceToken,
                region,
                // sessionId is the DynamoDB relay-session identifier.
                // It is deliberately not inferred from tunnelId.
                sessionId: querySessionId
            });

            return;
        }

        const activeTunnel = TunnelState.getActiveTunnel();

        if (activeTunnel) {
            setTunnelData({
                ...activeTunnel,
                // Preserve sessionId if the active tunnel object has it.
                sessionId: activeTunnel.sessionId || null
            });
        } else {
            toast.error('No active tunnel session found');
            window.close();
        }
    }, [location.search]);

    // Run terminal-exit handling once, and only for the owner.
    const handleTerminalExit = useCallback(() => {
        if (
            isDuplicateTabRef.current ||
            !ownsLockRef.current ||
            exitHandledRef.current
        ) {
            return;
        }

        exitHandledRef.current = true;

        notifyTerminalClosed();

        const tunnel = TunnelState.getActiveTunnel();

        if (tunnel) {
            TunnelState.clearActiveTunnel(tunnel.thingName);

            // Best effort only: browsers may terminate asynchronous
            // requests while closing a tab.
            closeTunnel({
                thingName: tunnel.thingName,
                tunnelId: tunnel.tunnelId
            }).catch((error) => {
                console.error(
                    'Failed to close tunnel on terminal exit:',
                    error
                );
            });
        }

        releaseOwnLock();
    }, [notifyTerminalClosed, releaseOwnLock]);

    // Handle browser-tab closure.
    useEffect(() => {
        window.addEventListener('pagehide', handleTerminalExit);
        window.addEventListener('beforeunload', handleTerminalExit);

        return () => {
            window.removeEventListener('pagehide', handleTerminalExit);
            window.removeEventListener(
                'beforeunload',
                handleTerminalExit
            );
        };
    }, [handleTerminalExit]);

    const handleConnectionClose = () => {
        if (isDuplicateTabRef.current) {
            window.close();
            return;
        }

        handleTerminalExit();

        toast.info('SSH connection closed');

        setTimeout(() => {
            window.close();
        }, 2000);
    };

    // Never render an active terminal in a duplicate tab.
    if (isDuplicateTab) {
        return (
            <div className="ssh-page-loading">
                <div>
                    SSH terminal is already open in another tab.
                </div>

                <button
                    type="button"
                    className="btn btn-secondary mt-3"
                    onClick={() => window.close()}
                >
                    Close Tab
                </button>
            </div>
        );
    }

    if (!tunnelData) {
        return (
            <div className="ssh-page-loading">
                <div className="ssh-loading-spinner"></div>
                <div>Loading SSH Terminal...</div>
            </div>
        );
    }

    if (!tunnelData.sessionId) {
        return (
            <div className="ssh-page-loading">
                <div>
                    Missing relay session ID. The URL or active tunnel data
                    must include the sessionId returned by the relay-session
                    creation API.
                </div>
                <button
                    type="button"
                    className="btn btn-secondary mt-3"
                    onClick={() => window.close()}
                >
                    Close Tab
                </button>
            </div>
        );
    }

    return (
        <div className="ssh-terminal-page">
            <div className="ssh-page-header">
                <div className="ssh-page-title">
                    <span className="ssh-device-name">
                        {tunnelData.thingName}
                    </span>

                    <span className="ssh-tunnel-id">
                        Tunnel: {tunnelData.tunnelId}
                    </span>
                </div>

                <button
                    type="button"
                    className="btn btn-danger ssh-close-btn"
                    onClick={() => {
                        handleTerminalExit();
                        window.close();
                    }}
                >
                    Close Terminal
                </button>
            </div>

            <div className="ssh-page-content">
                <SSHTerminal
                    sessionId={tunnelData.sessionId}
                    sourceToken={tunnelData.sourceToken}
                    region={tunnelData.region}
                    thingName={tunnelData.thingName}
                    onConnectionClose={handleConnectionClose}
                />
            </div>
        </div>
    );
};

export default SSHTerminalPage;
